import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { useI18n } from '../../contexts/I18nContext';
import { generateDownloadFilename } from '../../utils/fileUtils';
import LoginModal from '../Login/LoginModal';
import './ImageUpscaler.scss';
import './ImageUpscaler.mobile.scss';
import EnhanceButtonWithCredits from './parts/EnhanceButtonWithCredits';
import ShareTextModal from '../common/ShareTextModal';
import { API_BASE_URL } from '../../lib/constants';

// ImageUpscaler 组件，用于处理图像放大功能
const ImageUpscaler = () => {
  const { t } = useI18n();
  
  // 定义组件状态
  const [image, setImage] = useState(null); // 存储上传的图片
  const [imageFile, setImageFile] = useState(null); // 存储原始文件
  const [scale, setScale] = useState(2); // 存储放大比例
  const [selectedEnhanceTypes, setSelectedEnhanceTypes] = useState(['denoise']); // 选中的增强类型
  const [isProcessing, setIsProcessing] = useState(false); // 处理状态
  const [result, setResult] = useState(null); // 处理结果
  const [, setProcessingId] = useState(null); // processingId was unused, keeping setter
  const [processingProgress, setProcessingProgress] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMediaUrl, setShareMediaUrl] = useState('');
  
  
  // 增强类型定义（使用国际化）
  const enhanceTypes = {
    'denoise': t('imageUpscaler.denoise'),
    'face_enhance': t('imageUpscaler.face_enhance'),
    'sharpen': t('imageUpscaler.sharpen'),
    'color_enhance': t('imageUpscaler.color_enhance'),
    'detail_enhance': t('imageUpscaler.detail_enhance')
  };

  // 增强类型的提示词模板映射（参考App端逻辑）
  const enhancePromptTemplates = {
    'face_enhance': 'Enhance facial features, improve skin texture, reduce noise, and sharpen facial details while maintaining natural appearance',
    'denoise': 'Remove noise and grain from the image while preserving important details and textures',
    'sharpen': 'Sharpen the image to enhance clarity and definition of edges and details',
    'color_enhance': 'Enhance colors, improve contrast and saturation while maintaining natural color balance',
    'detail_enhance': 'Enhance image quality, improve clarity, and optimize visual appearance'
  };

  // 构建增强提示词
  const buildEnhancementPrompt = (selectedTypes) => {
    if (!selectedTypes || selectedTypes.length === 0) {
      return 'Enhance image quality, improve clarity, and optimize visual appearance';
    }

    // 如果只有一个类型，直接返回对应的提示词
    if (selectedTypes.length === 1) {
      return enhancePromptTemplates[selectedTypes[0]] || 'Enhance image quality, improve clarity, and optimize visual appearance';
    }

    // 多个类型时，组合提示词
    const prompts = selectedTypes.map(type => enhancePromptTemplates[type]).filter(Boolean);
    if (prompts.length === 0) {
      return 'Enhance image quality, improve clarity, and optimize visual appearance';
    }

    return prompts.join('. ') + '.';
  };

  // 使用统一的认证检查Hook
  const { checkAuthAndExecute, showLoginModal, handleCloseLogin } = useAuthCheck();

  // 组件挂载时重置滑动位置
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 处理图片上传事件
  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImage(URL.createObjectURL(file));
      setResult(null); // 清除之前的结果
    }
  };

  // 处理图片放大
  const handleEnhance = async () => {
    if (!imageFile) {
      toast.error(t('imageUpscaler.uploadImageFirst'), {
        style: {
          background: '#2a2a2e',
          color: '#fff',
          border: '1px solid #333'
        }
      });
      return;
    }

    // 使用统一的认证检查
    checkAuthAndExecute(async () => {
      setIsProcessing(true);
      setResult(null);
      setProcessingProgress(0);
      
      // 模拟进度更新
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 90) {
            return 90; // 保持在90%直到实际完成
          }
          // 确保进度只能向前，每次增加2-8%
          const increment = Math.random() * 6 + 2; // 2-8之间的随机增量
          const newProgress = Math.min(prev + increment, 90); // 确保不超过90%
          return newProgress;
      });
    }, 1000);

    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('scaleFactor', scale);
      formData.append('prompt', buildEnhancementPrompt(selectedEnhanceTypes));

      const response = await fetch('/api/image-upscale/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          toast.error(t('imageUpscaler.loginExpired'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
          // 清除过期token并重定向到登录页面
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        } else if (data.code === 'INSUFFICIENT_CREDITS') {
          toast.error(data.error, {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
        } else {
          toast.error(data.error || t('imageUpscaler.imageUpscaleFailed'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
        }
        return;
      }

      setProcessingId(data.id);

      // 轮询检查处理状态
      pollForResult(data.id, progressInterval);

    } catch (error) {
      toast.error(t('imageUpscaler.networkError'), {
        style: {
          background: '#2a2a2e',
          color: '#fff',
          border: '1px solid #333'
        }
      });
      clearInterval(progressInterval);
      setProcessingProgress(0);
    }
    });
  };

  // 轮询检查处理结果
  const pollForResult = async (id, progressInterval) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('imageUpscaler.loginExpired'), {
        style: {
          background: '#2a2a2e',
          color: '#fff',
          border: '1px solid #333'
        }
      });
      return;
    }

    const maxAttempts = 30; // 最多轮询30次
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/image-upscale/status/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.status === 401) {
          toast.error(t('imageUpscaler.loginExpired'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }

        if (!response.ok) {
          throw new Error(t('imageUpscaler.queryStatusFailed'));
        }

        const data = await response.json();

        if (data.status === 'completed' || data.status === 'COMPLETED') {
          setResult(data);
          setShareMediaUrl(data.shareUrl || '');
          setProcessingProgress(100);
          clearInterval(progressInterval);
          setIsProcessing(false);
          toast.success(t('imageUpscaler.imageUpscaleComplete'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
          return; // 确保在完成状态时立即返回，停止轮询
        } else if (data.status === 'failed' || data.status === 'FAILED') {
          clearInterval(progressInterval);
          setProcessingProgress(0);
          setIsProcessing(false);
          toast.error(data.errorMessage || t('imageUpscaler.imageUpscaleFailed'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
          return;
        }

        // 继续轮询
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // 2秒后再次检查
        } else {
          clearInterval(progressInterval);
          setProcessingProgress(0);
          setIsProcessing(false);
          toast.error(t('imageUpscaler.processingTimeout'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
        }
      } catch (error) {
        clearInterval(progressInterval);
        setProcessingProgress(0);
        setIsProcessing(false);
        toast.error(t('imageUpscaler.queryStatusFailed'), {
          style: {
            background: '#2a2a2e',
            color: '#fff',
            border: '1px solid #333'
          }
        });
      }
    };

    poll();
  };

  // 下载图片
  const handleDownload = () => {
    if (result && result.upscaledImageUrl) {
      // 确保使用完整的后端URL
      const downloadUrl = result.upscaledImageUrl.startsWith('http') 
        ? result.upscaledImageUrl 
        : `${API_BASE_URL}${result.upscaledImageUrl}`;
      
      // 使用fetch下载图片并创建blob对象，确保强制下载
      fetch(downloadUrl)
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const filename = generateDownloadFilename();
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('Download image failed:', error);
          toast.error(t('imageUpscaler.downloadFailed'), {
            style: {
              background: '#2a2a2e',
              color: '#fff',
              border: '1px solid #333'
            }
          });
        });
    }
  };

  return (
    <div className="image-upscaler">
      {/* 主体内容区域 */}
      <main className="upscaler-main-content">
        <h1>{t('imageUpscaler.title')}</h1>
        <p>{t('imageUpscaler.subtitle')}</p>
        
        <div className="workspace">
          {/* 左侧控制面板 */}
          <div className="controls">
            <h3>{t('imageUpscaler.details')}</h3>
            <div className="upload-area">
              <input type="file" onChange={handleImageUpload} accept="image/*" id="file-upload" style={{display: 'none'}} />
              <label htmlFor="file-upload" className="upload-label">
                <div className="upload-icon"></div>
                <p>{t('imageUpscaler.uploadText')}</p>
                <span>{t('imageUpscaler.uploadFormats')}</span>
              </label>
            </div>
            
            <div className="enhance-types">
              <h3>{t('imageUpscaler.enhanceTypes')}</h3>
              <div className="enhance-type-chips">
                {Object.entries(enhanceTypes).map(([type, label]) => (
                  <button
                    key={type}
                    className={`enhance-chip ${selectedEnhanceTypes.includes(type) ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedEnhanceTypes(prev => 
                        prev.includes(type) 
                          ? prev.filter(t => t !== type)
                          : [...prev, type]
                      );
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="scale-options">
              <h3>{t('imageUpscaler.factor')}</h3>
              <div className="scale-buttons">
                <button onClick={() => setScale(2)} className={scale === 2 ? 'active' : ''}>2x</button>
                <button onClick={() => setScale(4)} className={scale === 4 ? 'active' : ''}>4x</button>
                <button onClick={() => setScale(8)} className={scale === 8 ? 'active' : ''}>8x</button>
              </div>
            </div>

            <EnhanceButtonWithCredits 
              isProcessing={isProcessing}
              processingProgress={processingProgress}
              onClick={handleEnhance}
              disabled={isProcessing || !imageFile}
              scale={scale}
            />
          </div>

          {/* 右侧预览区域 */}
          <div className="preview">
            <h3>{t('imageUpscaler.preview')}</h3>
            <div className="image-preview-area">
              {image ? (
                <div className="image-container">
                  <img src={image} alt="Preview" />
                  {isProcessing && (
                    <div className="processing-overlay">
                      <div className="spinner"></div>
                      <p>{t('imageUpscaler.processingOverlay')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p>{t('imageUpscaler.noImageUploaded')}</p>
              )}
            </div>
            
            {/* 显示处理结果 */}
            {result && result.upscaledImageUrl && (
              <div className="result-section">
                <h4>{t('imageUpscaler.resultTitle')} ({result.scaleFactor}x)</h4>
                <div className="result-image" onClick={() => {
                  const url = result.upscaledImageUrl.startsWith('http') 
                    ? result.upscaledImageUrl 
                    : `${API_BASE_URL}${result.upscaledImageUrl}`;
                  if (url) window.open(url, '_blank');
                }} style={{ position: 'relative' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!shareMediaUrl) return;
                      setShareOpen(true);
                    }}
                    disabled={!shareMediaUrl}
                    style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 8, border: '1px solid #444', background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: shareMediaUrl ? 'pointer' : 'not-allowed', opacity: shareMediaUrl ? 1 : 0.6 }}
                    aria-label={t('creationRecord.share')}
          >
            <img src="/icons/share.svg" alt={t('creationRecord.share')} style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)', padding: 0 }} />
          </button>
                  <img 
                    src={result.upscaledImageUrl.startsWith('http') 
                      ? result.upscaledImageUrl 
                      : `${API_BASE_URL}${result.upscaledImageUrl}`
                    } 
                    alt="Upscaled" 
                    style={{ cursor: 'zoom-in' }}
                  />
                </div>
                <button className="download-button" onClick={handleDownload}>
                  {t('imageUpscaler.downloadButton')}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 功能介绍区域 */}
      <section className="features-section">
        <h2>{t('imageUpscaler.featuresTitle')}</h2>
        <div className="features-grid">
          {/* 功能一 */}
          <div className="feature-item">
            <h4>{t('imageUpscaler.feature1Title')}</h4>
            <p>{t('imageUpscaler.feature1Desc')}</p>
          </div>
          {/* 功能二 */}
          <div className="feature-item">
            <h4>{t('imageUpscaler.feature2Title')}</h4>
            <p>{t('imageUpscaler.feature2Desc')}</p>
          </div>
          {/* 功能三 */}
          <div className="feature-item">
            <h4>{t('imageUpscaler.feature3Title')}</h4>
            <p>{t('imageUpscaler.feature3Desc')}</p>
          </div>
          {/* 功能四 */}
          <div className="feature-item">
            <h4>{t('imageUpscaler.feature4Title')}</h4>
            <p>{t('imageUpscaler.feature4Desc')}</p>
          </div>
        </div>
      </section>

      {/* 使用指南区域 */}
      <section className="how-to-use-section">
        <h2>{t('imageUpscaler.howToUseTitle')}</h2>
        <div className="steps">
          {/* 步骤一 */}
          <div className="step">
            <div className="step-number">1</div>
            <h3>{t('imageUpscaler.step1Title')}</h3>
            <p>{t('imageUpscaler.step1Desc')}</p>
          </div>
          {/* 步骤二 */}
          <div className="step">
            <div className="step-number">2</div>
            <h3>{t('imageUpscaler.step2Title')}</h3>
            <p>{t('imageUpscaler.step2Desc')}</p>
          </div>
          {/* 步骤三 */}
          <div className="step">
            <div className="step-number">3</div>
            <h3>{t('imageUpscaler.step3Title')}</h3>
            <p>{t('imageUpscaler.step3Desc')}</p>
          </div>
        </div>
      </section>

      {/* 常见问题区域 */}
      <section className="faq-section">
        <h2>{t('imageUpscaler.faqTitle')}</h2>
        <div className="faq-list">
          {/* 问题一 */}
          <div className="faq-item">
            <p>{t('imageUpscaler.faq1')}</p>
          </div>
          {/* 问题二 */}
          <div className="faq-item">
            <p>{t('imageUpscaler.faq2')}</p>
          </div>
          {/* 问题三 */}
          <div className="faq-item">
            <p>{t('imageUpscaler.faq3')}</p>
          </div>
        </div>
      </section>
      
      {/* 登录弹窗 */}
      <LoginModal
          isOpen={showLoginModal}
          onClose={handleCloseLogin} 
      />
      <ShareTextModal
        open={shareOpen}
        title={t('creationRecord.enterShareText')}
        placeholder={t('creationRecord.enterShareText')}
        maxLength={2000}
        platform={null}
        mediaUrl={shareMediaUrl}
        mediaType="image"
        by=""
        onConfirm={() => { setShareOpen(false); }}
        onCancel={() => { setShareOpen(false); }}
      />
    </div>
  );
};

export default ImageUpscaler;
